// @flow
import Promise from 'bluebird';
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import * as d3 from 'd3';

import store from '../../store';
import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_GET_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../store/modules/overview-panel/types';
import api from '../api';
import DataTable from '../core/charts/Datatable';
import dataBoard from '../core/databoard';
import filters from '../core/filters';
import infoBoard from '../core/infoboard';
import worldMap from '../core/map';
import ColorBrewer from '../core/tools/colorbrewer';
import ColorFlatUI from '../core/tools/colorflatui';
import { getSegmentTheme } from '../core/tools/segment';
import translate from '../core/tools/translate';
import environment from '../environment';

import { FeaturePermission } from '../../constants/feature.permission.helper';
import { SaleTypeEnum } from '../../enuns/saleType.enum';
import Storage from '../storage';
import Workspace from '../workspaces';
import { ControlType, Item } from '../workspaces/item';
import { CoreControl } from './CoreControl';
import { VectorType } from './VectorType';
import { notify, NOTIFY_TYPE } from '../shared';
import { LocaleTypeEnum } from '../../enuns/localeType.enum';
import { openTagStyleEditor } from '../core/dialog/tag-style-editor';
import { clearTagStyles } from '../layers/Leaflet/VectorLayer/TagStyleStorage';
import { CustomLegendLayer } from '../layers/Leaflet/CustomLegendLayer';
import { getObjectThemeColor } from '../layers/Leaflet/VectorLayer/VectorLayerUtils';
declare var $: any;
declare var kendo: any;

const LayerInfo = name => require('../info').get(name);
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);
const GetLayer = (name, currentMap = worldMap.getCurrent()) => {
    return require('../layers/index').default.get(name, currentMap);
};

export class ShapesControl extends CoreControl {
    _tags: any;
    _cachePages: any;
    _date: any;

    constructor(options: any) {
        super(options);
        this._manualPaints = new Map();
        this._manualFeatures = new Map();
        this._areaPalettes = new Map();
        this._deliveredAreasLegends = new Map();
        this._revertingAreaPalette = false;
        this.saleType = null;
        this.brandId = null;

        (this: any)._calculateScale = this._calculateScale.bind(this);
        (this: any)._change = this._change.bind(this);
        (this: any)._changePeriod = this._changePeriod.bind(this);
        (this: any)._changeTheme = this._changeTheme.bind(this);
        (this: any)._colorizeStaticShape = this._colorizeStaticShape.bind(this);
        (this: any)._disable = this._disable.bind(this);
        (this: any)._filterCallback = this._filterCallback.bind(this);
        (this: any)._getCheckboxOptions = this._getCheckboxOptions.bind(this);
        (this: any)._getLayerOptions = this._getLayerOptions.bind(this);
        (this: any)._toggleFilterByAreas = this._toggleFilterByAreas.bind(this);
        (this: any)._toggleFilterByLocation = this._toggleFilterByLocation.bind(this);
        (this: any)._toggleFilterByPoints = this._toggleFilterByPoints.bind(this);
        (this: any)._toggleLabel = this._toggleLabel.bind(this);
        (this: any).colorize = this.colorize.bind(this);
        (this: any).createFilter = this.createFilter.bind(this);
        (this: any)._handleFilterValueChange = this._handleFilterValueChange.bind(this);
        (this: any).getTypeValue = this.getTypeValue.bind(this);
        (this: any).reload = this.reload.bind(this);
        (this: any).select = this.select.bind(this);
        (this: any).setFillColor = this.setFillColor.bind(this);
        (this: any).setLineColor = this.setLineColor.bind(this);
        (this: any).setLineWeight = this.setLineWeight.bind(this);
        (this: any)._getFilterConfig = this._getFilterConfig.bind(this);
        (this: any).setRange = this.setRange.bind(this);
        (this: any).toggleOverview = this.toggleOverview.bind(this);
        (this: any).getDate = this.getDate.bind(this);
        (this: any)._downloadVehicleFleetArea = this._downloadVehicleFleetArea.bind(this);
    }

    hasDynamicThemes() {
        return _.size(this.options.themes) > 0;
    }

    hasVehicleFleetArea() {
        return this.options.themes.vehicleFleetArea;
    }

    init() {
        this._tags = {
            tagName: {
                property: 'Nome',
                box: true,
                size: 9,
                offsetY: -5,
                ignore: value => Number(value) === 0
            },
            tagValue: {
                property: 'Total',
                offsetY: 14,
                size: 10,
                colorize: true,
                bold: true,
                box: true,
                ignore: value => Number(value) === 0
            },
            tagDrugstore: {
                property: 'IsDrugStore',
                box: true,
                size: 11,
                offsetY: -26,
                colorize: true,
                bold: true
            }
        };

        const values = {
            help: translate(`Help.${this.options.name}`),
            id: `layer-${this.options.id}`,
            input: 'checkbox',
            label: translate(`Common.${this.options.name}`),
            name: this.options.name,
            layer: this.getLayer,
            type: this.options.type
                ? this.options.type
                : this.hasDynamicThemes()
                ? 'vector-layers-extended'
                : 'vector-layers',
            overview: this.options.overview ? this.toggleOverview : null,
            change: this._change,
            tab: () => {
                return $(`#${this.options.id}-tab-link`);
            },
            scheme: this.getTypeValue,
            fillColor: {
                name: 'fillColor',
                change: this.setFillColor
            },
            lineColor: {
                name: 'lineColor',
                change: this.setLineColor
            },
            lineWeight: {
                change: this.setLineWeight
            },
            range: {
                change: this.setRange
            },
            filter: this.options.filter && this.hasDynamicThemes() ? this.createFilter : false,
            links: {},
            checkbox: this._getCheckboxOptions(),
            dropdown: [],
            multiselect: []
        };

        if (this.hasDynamicThemes() && this.options.name !== 'Voronoi') {
            values.links[translate('Action.ShowDateFrom')] = {
                id: 'layer-customers-date',
                name: 'filterYear',
                text: `${moment(this.getDate().start).format('MMM/YYYY')} -> ${moment(this.getDate().end).format(
                    'MMM/YYYY'
                )}`,
                value: { start: this.getDate().start.toDate(), end: this.getDate().end.toDate() },
                change: this._changePeriod,
                type: 'datepicker'
            };
        }

        if (_.size(this.options.types)) {
            const dataSource = _.map(this.options.types, t => ({ text: t.name, value: t.id }));
            values.dropdown.push({
                name: 'list',
                dataSource,
                change: this.select,
                visible: _.size(this.options.types) >= 1
            });
        }

        if (_.size(this.options.themes)) {
            const dataSource = _.reduce(
                this.options.themes,
                (acc, theme) => {
                    const themes = _.size(this.options.types)
                        ? this.options.types[_.keys(this.options.types)[0]].themes
                        : _.map(this.options.themes, e => e.id);

                    if (
                        _.indexOf(themes, theme.id) > -1 ||
                        _.indexOf(
                            _.map(themes, x => x.id),
                            theme.id
                        ) > -1
                    ) {
                        acc.push({ text: translate(theme.name), value: theme.id });
                    }

                    return acc;
                },
                _.size(this.options.types) ? [{ text: translate('Common.Theme'), value: 0 }] : []
            );

            values.dropdown.push({
                extends: true,
                name: 'data',
                dataSource,
                help: translate('Help.ThemeInfo'),
                change: this._changeTheme,
                disable: this._disable,
                separator: _.size(this.options.types) > 0
            });

            values.dropdown.push({
                extends: true,
                name: 'periods',
                dataSource: [{ text: 'Período', value: '0' }],
                change: period => {
                    this._getPeriodByThemes()
                        .filter(p => parseInt(p.value) === parseInt(period))
                        .map(p => this._setPeriod(p.text));

                    this._updatePopover();
                    this.reload();
                },
                separator: true,
                visible: false,
                disable: this._disable
            });

            if (this.hasVehicleFleetArea()) {
                values.links[translate('Action.DownloadReport')] = {
                    id: 'layer-shapescontrol-download-vehiclefleet',
                    name: 'vehicleFleetAreaDownload',
                    value: 'Parque Circulante',
                    text: 'Parque Circulante',
                    hide: true,
                    dataSource: [{ text: 'Excel', value: 'Parque Circulante' }],
                    change: this._downloadVehicleFleetArea
                };
            }
        }

        if (!this._instance) {
            this._instance = new VectorType(values);
        }

        this._cachePages = new Map();
    }

    _downloadVehicleFleetArea(options) {
        const bbox = worldMap.getCurrentBbox();
        const localeFiltered = environment.get('LOCALE.FILTERED');
        const exportVehicleFleetArea =
            localeFiltered &&
            ![LocaleTypeEnum.Pais, LocaleTypeEnum.Regiao, LocaleTypeEnum.UF].includes(localeFiltered.type);

        if (exportVehicleFleetArea) {
            api.shapes.DownloadVehicleFleetArea(localeFiltered.id, bbox);
            return;
        }

        notify(NOTIFY_TYPE.LOG, translate('Validation.ExportFleetAreaWarning'));
    }

    _getPeriodByThemes() {
        const themeKey = this._getThemeKeySelected();
        const periods = [
            { text: 'Período', value: 0 },
            ...environment
                .get('CONFIG.FILTERS.FILTERBY')
                .filter(x => x.type === `Areas-${themeKey}`)
                .flatMap(x => x.options)
                .map(x => ({ text: x.nome, value: x.id }))
        ];

        return periods;
    }

    _loadPeriodByTheme() {
        const periods = this._getPeriodByThemes();
        const lastValue = periods.length - 1;
        this.getControls().dropdown.periods.input.setDataSource(periods);
        this.getControls().dropdown.periods.input.select(periods[lastValue].value);
        this._setPeriod(periods[lastValue].text);
    }

    _setPeriod(periods) {
        this.options.period = {
            initial: periods.split('-')[0],
            final: periods.split('-')[1]
        };
    }

    _getThemeKeySelected() {
        const themes = Object.keys(this.options.themes).map(t => ({
            id: this.options.themes[t].id,
            key: this.options.themes[t].key
        }));
        const themeValue = this.getControls().dropdown.data.input.value();
        const themeKey = themes.filter(x => x.id === parseInt(themeValue))[0].key;
        return themeKey;
    }

    _logAction(actionName: string) {
        api.general.log.action(
            environment._getContextEnum(this.options.name),
            actionName,
            JSON.stringify({ TipoLocalidade: this.getLocaleName(), Tema: this.getThemeText() })
        );
    }

    show(options: any = {}, features: any = this.getLayer().getCurrentFeatures()) {
        if ($(`#${this.options.id}-collapse`).length === 0 || options.refresh) {
            if (options.empty) {
                this._view('create', null);
            } else {
                this._view('create', features);
            }
        }

        if (options.show) {
            infoBoard.open();
            if (options.id) {
                $(`#${this.options.id}-title-${options.id}`).trigger('click');
            }

            setTimeout(() => {
                $(`#${this.options.id}-tab-link`).trigger('click');
            }, 100);
        }
    }

    select(value: number, options: any) {
        if (_.isString(value)) {
            value = Number(value);
        }

        if (options && !_.isString(options)) {
            console.log('CHANGE LAYER OPTIONS');
            this.getLayer().setOptions(options);
        }

        if (this.getLayer().visible()) {
            this._logAction('Update Localidade');
        }
        this._resetTheme(value);
        this._uncheckAll();
        this._unhighlightArea(false);

        try {
            const { remote, scaleRange } = this.getLayer().options;

            scaleRange.setOptions({ id: value });
            remote.params.locationType = value;
        } catch (err) {
            console.log(err);
        }

        if (this.check() && this.getLayer().visible()) {
            this.getLayer().reset();
        }
    }

    _resetTheme(value: number) {
        try {
            if (typeof value === 'undefined' || value === null || value < 0) {
                value = this.getControls().dropdown.list.value;
            }

            if (this.getControls().dropdown.data) {
                const dataSource = new kendo.data.DataSource({
                    data: _.reduce(
                        this.options.themes,
                        (acc, theme) => {
                            const t = (_.find(this.options.types, e => e.id === value): any);
                            if (t && t.themes && _.indexOf(t.themes, theme.id) > -1) {
                                acc.push({ text: theme.name, value: theme.id });
                            }

                            return acc;
                        },
                        [{ text: 'Tema', value: 0 }]
                    )
                });

                dataSource.read();
                const currentIndex = _.findIndex(
                    dataSource.view(),
                    d => d.value === Number(this.getControls().dropdown.data.value)
                );

                this.getControls().dropdown.data.input.setDataSource(dataSource);
                this.getControls().dropdown.data.input.select(currentIndex > 0 ? currentIndex : 0);
                this.getControls().dropdown.data.index = currentIndex > 0 ? currentIndex : 0;
                this.getControls().dropdown.data.text =
                    currentIndex > 0 ? this.getControls().dropdown.data.text : 'None';
                this.getControls().dropdown.data.value = currentIndex > 0 ? this.getControls().dropdown.data.value : 0;
                this._setFilterButtonVisibility(false);

                this._updatePopover();
            }
        } catch (err) {
            console.log(err);
        }
    }

    removeManualPaint(id) {
        if (id) {
            this._manualPaints.delete(String(id));
            this._manualFeatures.delete(String(id));
        }
    }

    colorize(featureCollection: any, layer: any = this.getLayer()) {
        layer.setOptions(this._tagStyleLayerOptions());

        const id = layer.getMap()._id;
        const themeId = this.getThemeValue(id);
        const deliveredIds = new Set(_.map(_.get(featureCollection, 'features', []), f => String((f.properties || {}).Id)));

        const injectManualFeatures = (collection) => {
            this._manualFeatures.forEach((f, featureId) => {
                const existing = _.find(collection.features, feat => String(feat.properties.Id) === String(featureId));
                if (existing) {
                    if (!themeId) {
                        existing.properties._fillColor = this._manualPaints.get(featureId);
                    }
                } else {
                    const cloned = _.cloneDeep(f);
                    if (!themeId) {
                        cloned.properties._fillColor = this._manualPaints.get(featureId);
                    }
                    collection.features.push(cloned);
                }
            });
            return collection;
        };

        const fc = injectManualFeatures(_.cloneDeep(featureCollection));

        return new Promise(resolve => {
            if (!themeId) {
                resolve(this._paintDeliveredAreas(fc, id, deliveredIds));
            } else {
                this._removeDeliveredAreasLegend(id);

                fc.features.forEach(f => {
                    delete f.properties._fillColor;
                });

                this.options.request(this._getLayerOptions(fc, themeId, id)).then(res => {
                    let result;
                    if (!_.size(res)) {
                        result = this._setFeaturesValue(fc, {});
                    } else {
                        this._setFilterButtonVisibility(true);
                        result = this._colorizeStaticShape(id, fc, res);
                    }

                    resolve(result);
                });
            }
        });
    }

    setRange() {
        if (this.getThemeValue() && this.check() && this.getLayer().visible()) {
            const layer = this.getLayer();
            const id = layer.getMap()._id;
            const featureCollection = this._colorizeStaticShape(id, layer.toGeoJSON(), this._cachePages.get(id));
            layer.setFeatures(featureCollection, true);
            this._applyManualColors(layer);
        }
    }

    setFillColor(value: any) {
        // console.time('NeoGIG.Feature.Shapes -> setFillColor');
        const layer = this.getLayer();

        const color = value.split(':')[0];
        const index = value.split(':')[1];

        const { symbology } = layer.options;
        symbology.theme = ColorBrewer[color][index];

        if (_.isArray(symbology.theme)) {
            this._areaPalettes.set(layer.getMap()._id, symbology.theme);
        }

        layer.setOptions({ symbology });

        if (this.check() && this.getLayer().visible()) {
            if (this.isPaintingDeliveredAreas() && !this.getThemeValue()) {
                this.reload();
            } else {
                layer.reload();
            }
        }

        // console.timeEnd('NeoGIG.Feature.Shapes -> setFillColor');
    }

    setLineColor(value: any) {
        // console.time('NeoGIG.Feature.Shapes -> setLineColor');
        const layer = this.getLayer();

        const color = value.split(':')[0];
        const index = value.split(':')[1];

        const { symbology } = layer.options;
        symbology.style.color = ColorFlatUI[color][index][0];

        layer.setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            layer.reload();
        }

        // console.timeEnd('NeoGIG.Feature.Shapes -> setLineColor');
    }

    setLineWeight(value: any) {
        // console.time('NeoGIG.Feature.Shapes -> setLineWeight');
        const layer = this.getLayer();

        const { symbology } = layer.options;
        symbology.style.weight = value;

        layer.setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            layer.reload();
        }

        // console.timeEnd('NeoGIG.Feature.Shapes -> setLineWeight');
    }

    getDate(id: number = this.getLayer().getMap()._id) {
        if (_.isUndefined(this._date)) {
            this._date = new Map();
        }

        if (!this._date.has(id)) {
            if (id === 0)
                this._date.set(id, {
                    start: moment(environment.get('CONFIG.FILTERS.ENDYEAR')).startOf('year'),
                    end: moment(environment.get('CONFIG.FILTERS.ENDYEAR'))
                });
            else this._date.set(id, this._date.get(id - 1));
        }

        return this._date.get(id);
    }

    getThemeValue(id: number = this.getLayer().getMap()._id) {
        return (() => {
            try {
                return Number(this.getControls(id).dropdown.data.value);
            } catch (err) {
                console.log(err);
                return '';
            }
        })();
    }

    getThemeText(id: number = this.getLayer().getMap()._id): string {
        try {
            return this.getControls(id).dropdown.data.text;
        } catch (error) {
            return '';
        }
    }

    getExportFileName(): string {
        return [
            environment.get('CONFIG.CLIENT'),
            'Painel de Dados',
            translate(`Common.${this.options.name}`),
            this.getThemeText(),
            `v${moment().format('YYYYMMDD')}`
        ]
            .filter(v => v)
            .join('_')
            .replace(/[\\/:*?"<>|]/g, '-');
    }

    getLocaleName(id: number = this.getLayer().getMap()._id): string {
        try {
            return this.getControls(id).dropdown.list.text;
        } catch (error) {
            return '';
        }
    }

    getTypeValue(realValue: boolean) {
        const _value = (() => {
            try {
                return Number(this.getControls().dropdown.list.value);
            } catch (err) {
                return environment.get('TYPE.AUTO');
            }
        })();

        if (realValue) {
            return _value;
        } else if (_value === environment.get('TYPE.AUTO')) {
            return (this.getZoomLocation('ibge') || {}).id;
        }

        return _value;
    }

    getZoomLocation(type: string = 'ibge') {
        const areas = {
            ibge: environment.get('CONFIG.FILTERS.AREAS_TYPES'),
            client: environment.get('CONFIG.FILTERS.MARKED_AREA_TYPES')
        };

        try {
            return _(areas[type])
                .filter(z => z.zoom)
                .find(z => _.inRange(worldMap.getZoom(), z.zoom.min - 1, z.zoom.max + 1));
        } catch (err) {
            console.log(err);
            return {};
        }
    }

    getTypeText() {
        try {
            return this.getControls().dropdown.list.text;
        } catch (err) {
            console.log(err);
            return 'None';
        }
    }

    reload(skipManualColors = false) {
        if (this.check() && this.getLayer().visible()) {
            const layer = this.getLayer();
            this._cachePages = new Map();
            this.colorize(layer.toGeoJSON(), layer).then(res => {
                if (skipManualColors) {
                    res.features.forEach(f => {
                        delete f.properties._fillColor;
                    });
                }
                layer.setFeatures(res, true);
                if (!skipManualColors) {
                    this._applyManualColors(layer);
                }
            });
        }
    }

    createFilter() {
        const filterConfig = this._getFilterConfig();
        const value = environment.get('CONFIG.FILTERS.SALES_TYPE')[0].value;
        if (value === SaleTypeEnum.Retail) {
            filterConfig.fields['channels'] = false;
            if (filterConfig.fields['companySizes']) {
                filterConfig.fields['companySizes'] = false;
            }
        }
        filters.create(filterConfig);
    }

    _handleFilterValueChange(payload) {
        const filterConfig = this._getFilterConfig();
        if (payload.fieldName === 'saleType') {
            this.saleType = payload.value;
            if (payload.value === SaleTypeEnum.Retail) {
                filterConfig.fields['channels'] = false;
                if (filterConfig.fields['companySizes']) {
                    filterConfig.fields['companySizes'] = false;
                }
            }

            filters.updateControls(filterConfig);
        }

        if (payload.fieldName === 'brands') {
            this.brandId = payload.value;
        }
    }

    _getFilterConfig() {
        const haveSalesTotal = environment.get("CONFIG.CLIENT_TOTAL_SALES");

        return {
            container: document.getElementById('filter-container'),
            id: `filter-${_.kebabCase(this.options.name)}-${this.getThemeValue()}`,
            label: translate(`Common.${this.options.name}`),
            type: this.options.name,
            direction: 'up',
            onSubmit: this._filterCallback,
            onValueChange: this._handleFilterValueChange,
            fields: {
                channels: this._checkThemes([
                    'brandsRegistration',
                    'industryRegistration',
                    'marketShare',
                    'invasionIndicator',
                    'brandArea'
                ]),
                sales: this._checkThemes([
                    'brandsRegistration',
                    'industryRegistration',
                    'marketShare',
                    'invasionIndicator',
                    'brandArea',
                    'segmentLeader',
                    'openPoint',
                ]),
                segments: !this._checkThemes([
                    'groupsArea',
                    'pib',
                    'ipcAuto',
                    'locationName',
                    'vehicleFleetArea',
                    'repairableFleetArea',
                    'socialClass',
                    'segmentLeader',
                ]),
                brands: {
                    visible: !this._checkThemes([
                        'groupsArea',
                        'brandArea',
                        'pib',
                        'brandsRegistration',
                        'ipcAuto',
                        'locationName',
                        'invasionIndicator',
                        'dealerRegistration',
                        'dealerMarketShare',
                        'vehicleFleetArea',
                        'repairableFleetArea',
                        'socialClass',
                    ]),
                    type: this._checkThemes(['marketShare', 'openPoint', 'locationName']) ? 'dropdown' : 'multiselect'
                },
                classification: !this._checkThemes([
                    'pib',
                    'brandsRegistration',
                    'openPoint',
                    'ipcAuto',
                    'vehicleFleetArea',
                    'repairableFleetArea',
                    'brandArea',
                ]),
                companySizes: this._checkThemes([
                    'brandsRegistration',
                    'industryRegistration',
                    'marketShare',
                    'invasionIndicator',
                    'brandArea',
                    'segmentLeader',
                    'openPoint',
                ]),
                fuelTypes: this._checkThemes([
                    'brandsRegistration',
                    'industryRegistration',
                    'marketShare',
                    'invasionIndicator',
                    'brandArea',
                    'segmentLeader'
                ]),
                specializationIndustry: true,
                vehicleFleetArea: this._checkThemes(['vehicleFleetArea', 'repairableFleetArea']),
                groups: this._checkThemes(['groupsArea']),
                socialClass: this._checkThemes(['socialClass']),
                typeSaleClassification: this._checkThemes(['brandsRegistration']),
            }
        };
    }

    toggleOverview() {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);

        const features = layer.getCurrentFeatures();

        if (!store.getters[OVERVIEW_PANEL_IS_VISIBLE] || !_.isEqual(this._overviewFeatures, features)) {
            api.general.log.action(environment._getContextEnum(this.options.name), 'Visualizar Gráfico');
        }

        if (
            (_.size(features) && !_.isEqual(this._overviewFeatures, features)) ||
            !store.getters[OVERVIEW_PANEL_IS_VISIBLE]
        ) {
            this.summary(features);
            this._overviewFeatures = _.cloneDeep(features);

            store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
        }
    }

    getButtonExport() {
        const exportCustomButton = {
            icon: 'download',
            text: 'custom',
            name: translate('Action.Export'),
            callback: function (mapId = worldMap.getCurrent()._id) {
                const optionsExport = Storage.get(`options-shapesControl-${mapId}`);
                const parameters = {
                    idLocalidades: optionsExport.Ids,
                    idSegments: optionsExport.idsCategorias,
                    idBandeiras: optionsExport.idsBandeiras,
                    idsSalesChannel: optionsExport.salesChannel
                        ? optionsExport.salesChannel.filter(x => x !== -9999)
                        : null,
                    idTipoLocalidade: optionsExport.idTipo,
                    anoMesInicio: optionsExport.anoMesInicio,
                    anoMesFim: optionsExport.anoMesFim,
                    tipoVenda: optionsExport.saleType,
                    idTheme: optionsExport.theme,
                    idsCompanySize: optionsExport.idsCompanySize,
                    idsFuelType: optionsExport.idsFuelType,
                    idIndustry: optionsExport.idIndustry,
                    typeSaleClassificationIds: optionsExport.typeSaleClassification,
                };
                api.themes.get.areas.salesExport(parameters);
            }
        };

        return exportCustomButton;
    }

    summary(features) {
        const themeId = this.getThemeValue();
        const isBrandAreaOrSegmentLeader = this._checkThemes(['brandArea', 'segmentLeader']);
        const isThemeWithoutCallbackToExport = this._checkThemes(['brandArea', 'segmentLeader', 'pib']);
        const data = this.calculate(features);

        const dataStructure = {
            row: translate('Common.Name'),
            column: [translate('Common.Total')]
        };

        const chartPie = new DataTable(data.perPercentage, dataStructure);
        const chartPieSecond = new DataTable(data.perSegments, dataStructure);
        const chartPieThird = new DataTable(data.perChannel, dataStructure);
        const chartColumn = new DataTable(data.perTotal, dataStructure);

        const id = worldMap.getCurrent()._id;
        const filterData = filters.get(`filter-${_.kebabCase(this.options.name)}-${themeId}`, id) || {};

        const dicSaleType: Array<{ saleType: SaleTypeEnum, text: string, idSaleType: number }> = [
            { saleType: SaleTypeEnum.Retail, text: translate('Common.Retail'), idSaleType: 1 },
            { saleType: SaleTypeEnum.DirectSales, text: translate('Common.DirectSales'), idSaleType: 2 },
            { saleType: SaleTypeEnum.Total, text: 'Total', idSaleType: 3 }
        ];
        let defaultSaleType = isAuthorized(FeaturePermission.SYSTEM.showDataRetail) ? 1 : 2;
        defaultSaleType = this._checkIfHasTotalSales() ? 3 : defaultSaleType;
        let saleTypeName = dicSaleType.findOrFirst(x => x.idSaleType === defaultSaleType).text;
        let saleType = (filterData.saleType && filterData.saleType.value) || defaultSaleType;

        if (this.saleType) {
            saleTypeName = dicSaleType.findOrFirst(x => x.idSaleType === this.saleType).text;
            saleType = this.saleType;
        }

        const mktShareFormat = this._checkThemes(['marketShare', 'brandArea', 'segmentLeader']) ? '{0:p1}' : '{0:n0}';
        const date = this.getDate();
        let options = {
            title: {
                table: `${translate(`Common.${this.options.name}`)}`,
                chartPie: this._mountNameChartPie(isBrandAreaOrSegmentLeader),
                chartColumn: `RANKING ${this.getThemeText()}`,
                chartPieSecond: `RANKING ${translate(`Common.Segments`)}`,
                chartPieThird: `${translate('Common.ChannelRanking')} (%)`
            },
            dash: true,
            fourthLevel: _.size(data.perSegments) > 0,
            fifthLevel: saleType === 2 && _.size(data.perChannel) > 0,
            titleHeader: true,
            titleHeaderMessage: `Sumário - ${this.getLocaleName()} - ${saleTypeName} | ${date.start.format(
                'MM/YYYY'
            )} - ${date.end.format('MM/YYYY')}`,
            data: [data.summary, chartPie, chartColumn, chartPieSecond, chartPieThird],
            format: [mktShareFormat, '{0:p1}', mktShareFormat, mktShareFormat],
            convertTable: false,
            columns: [
                {
                    field: 'name',
                    title: translate('Common.Name')
                },
                ...(isBrandAreaOrSegmentLeader
                    ? [
                          {
                              field: 'brand',
                              title: this._checkThemes(['brandArea'])
                                  ? translate('Common.Brand')
                                  : translate('Common.Segment'),
                              width: 140
                          }
                      ]
                    : []),
                {
                    field: 'total',
                    title: this._mountTotalColumnTitle(isBrandAreaOrSegmentLeader),
                    width: 100,
                    template: data =>
                        numeral(data.total).format(
                            this._checkThemes(['marketShare', 'brandArea', 'segmentLeader']) ? '0,0[.]0%' : '0,0'
                        )
                },
                ...(!this._checkThemes(['marketShare', 'brandArea', 'segmentLeader'])
                    ? [
                          {
                              field: 'percentage',
                              title: `${translate('Common.Total')} (%)`,
                              width: 100,
                              template: data => numeral(data.percentage).format('0,0[.]00%')
                          }
                      ]
                    : [])
            ],
            buttons: {
                // custom: exportCustomButton
                fullscreen: true,
                custom:
                    isAuthorized(FeaturePermission.LAYER_SELECTED_AREA.exportDataPanel) && !isThemeWithoutCallbackToExport
                        ? this.getButtonExport(this.options)
                        : undefined,
                export: isThemeWithoutCallbackToExport
            }
        };

        if (this._checkThemes(['vehicleFleetArea', 'repairableFleetArea'])) {
            options = {};
            options = {
                title: {
                    table: `${translate(`Common.${this.options.name}`)}`,
                    chartPie: `RANKING ${translate(`Common.Segments`)}`,
                    chartColumn: `RANKING ${this.getThemeText()}`
                },
                dash: true,
                titleHeader: true,
                titleHeaderMessage: `Sumário - ${this.getLocaleName()} | ${date.start.format(
                    'MM/YYYY'
                )} - ${date.end.format('MM/YYYY')}`,
                data: [data.summary, chartPieSecond, chartColumn],
                format: ['{0:n0}', '{0:p1}', '{0:n0}'],
                convertTable: false,
                columns: [
                    {
                        field: 'name',
                        title: translate('Common.Name')
                    },
                    {
                        field: 'total',
                        title: translate('Common.Total'),
                        width: 100,
                        template: data =>
                            numeral(data.total).format(this._checkThemes(['marketShare']) ? '0,0[.]0%' : '0,0')
                    },
                    ...[
                        {
                            field: 'percentage',
                            title: `${translate('Common.Total')} (%)`,
                            width: 100,
                            template: data => numeral(data.percentage).format('0,0[.]00%')
                        }
                    ]
                ],
                buttons: {
                    fullscreen: true
                }
            };
        }

        if (isBrandAreaOrSegmentLeader) {
            // usa as mesmas cores que pintam o shape (symbology.theme)
            const theme = this._getAreaThemePalette();

            options.colors = {
                1: data.perPercentage.map(r =>
                    getObjectThemeColor(theme, r[translate('Common.Name')], 'rgb(204,204,204)')
                )
            };
        }

        if (isThemeWithoutCallbackToExport) {
            options.nameFile = () => this.getExportFileName();
            options.exportHeader = `${translate(`Common.${this.options.name}`)} - ${translate(
                'Common.Summary'
            )} | ${date.start.format('MM/YYYY')} - ${date.end.format('MM/YYYY')} | ${saleTypeName}`.toUpperCase();
        }

        let options_shapes = this._getLayerOptions(this.getLayer().toGeoJSON(), themeId, id);
        options_shapes.Ids = _(features)
            .map(f => f.properties.Id)
            .value();
        options_shapes.theme = this.getThemeValue(id);
        options_shapes.saleTypeName = _.isUndefined(filterData.saleType) ? saleTypeName : filterData.saleType.text;
        options_shapes.saleType = saleType;

        if (filterData.industry && filterData.industry.value) {
            options_shapes.idIndustry = _.isArray(filterData.industry.value)
                ? filterData.industry.value.map(x => x.toString())
                : [filterData.industry.value.toString()];
        }

        if (filterData.channels && filterData.channels.value) {
            options_shapes.salesChannel = _.isArray(filterData.channels.value)
                ? filterData.channels.value.map(x => x.toString())
                : [filterData.channels.value.toString()];
        }

        Storage.save(`options-shapesControl-${id}`, options_shapes);

        dataBoard.show(options);
    }

    calculate(features) {
        const dataFlatten = _(features)
            .filter(f => f.properties.RegistryDetail)
            .map(f => f.properties.RegistryDetail)
            .flatten();

        const isBrandAreaOrSegmentLeader = this._checkThemes(['brandArea', 'segmentLeader']);

        const total = _.sumBy(features, f => f.properties.Total);
        let summary = _(features)
            .orderBy(f => f.properties.Total)
            .reverse()
            .map(f => ({
                name: f.properties.Nome,
                total: f.properties.Total,
                percentage: f.properties.Total / total
            }))
            .value();

        let detailByBrands = dataFlatten
            .groupBy(f => f.idBrand)
            .map((f, key) => {
                return {
                    idBrand: key,
                    nameBrand: f[0].nameBrand || '',
                    value: _.sumBy(f, 'valor')
                };
            })
            .value();

        let totalRegistration = _.sumBy(detailByBrands, 'value');
        let perPercentageResult = _(detailByBrands)
            .orderBy(f => f.value)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.nameBrand,

                [translate('Common.Total')]: f.value / totalRegistration
            }))
            .value();

        if (this._checkThemes(['pib'])) {
            perPercentageResult = this._calculatePibPercentage(features, total);
        }

        if (this._checkThemes(['marketShare'])) {
            perPercentageResult = _(features)
                .filter(f => f.properties.Total)
                .orderBy(f => f.properties.Total)
                .reverse()
                .map(f => ({
                    [translate('Common.Name')]: f.properties.Nome,
                    [translate('Common.Total')]: this._checkThemes(['marketShare'])
                        ? f.properties.Total
                        : f.properties.Total / total
                }))
                .value();
        }

        let perPercentage = perPercentageResult;
        let perTotal = _(features)
            .filter(f => f.properties.Total)
            .orderBy(f => f.properties.Total)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.properties.Nome,
                [translate('Common.Total')]: f.properties.Total
            }))
            .value();

        if (isBrandAreaOrSegmentLeader) {
            const brandRows = _(features)
                .filter(f => f.properties.Brand && f.properties.Brand !== 0)
                .map(f => ({
                    name: f.properties.Nome,
                    brand: f.properties.Brand,
                    total: _.toNumber(_.get(f, 'properties.RegistryDetail[0].decimal')) || 0
                }))
                .orderBy('total', 'desc')
                .value();

            summary = brandRows;

            perPercentage = _(brandRows)
                .groupBy('brand')
                .map((group, brand) => ({
                    [translate('Common.Name')]: brand,
                    [translate('Common.Total')]: _.size(group) / _.size(brandRows)
                }))
                .orderBy(translate('Common.Total'), 'desc')
                .value();

            perTotal = brandRows.map(f => ({
                [translate('Common.Name')]: f.name,
                [translate('Common.Total')]: f.total
            }));
        }

        const detailBySegments = dataFlatten
            .filter(f => !_.isEmpty(f.nameSegment))
            .groupBy(f => f.idSegment)
            .map((f, key) => {
                return {
                    idSegment: key,
                    nameSegment: f[0].nameSegment || '',
                    value: _.sumBy(f, 'valor')
                };
            })
            .value();

        const totalSegments = _.sumBy(detailBySegments, 'value');
        const perSegments = _(detailBySegments)
            .orderBy(f => f.value)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.nameSegment,

                [translate('Common.Total')]: f.value / totalSegments
            }))
            .value();

        const detailByChannel = dataFlatten
            .filter(f => !_.isEmpty(f.nameChannel))
            .groupBy(f => f.idChannel)
            .map((fm, key) => ({
                idChannel: key,
                nameChannel: fm[0].nameChannel,
                value: _.sumBy(fm, 'valor')
            }))
            .value();

        const totalChannel = _.sumBy(detailByChannel, 'value');
        const perChannel = _(detailByChannel)
            .orderBy(f => f.value)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.nameChannel,
                [translate('Common.Total')]: f.value / totalChannel
            }))
            .value();

        return {
            perPercentage,
            perTotal,
            summary,
            perSegments,
            perChannel
        };
    }

    _updatePopover() {
        try {
            this.getControls().button.popover.target[0].dataset.content = translate(
                `Help.${
                    (
                        _(this.options.themes)
                            .map((val, key) => ({
                                id: val.id,
                                key: val.key
                            }))
                            .find(val => val.id === this.getThemeValue()) || { key: 'ThemeInfo' }
                    ).key
                }`
            );
        } catch (err) {
            console.log('ERROR: ', err);
        }
    }

    _uncheckAll() {
        _.forEach(this.getControls().checkbox, control => {
            control.input.iCheck('uncheck');
        });
    }

    _change(e: any) {
        this._logAction(e.checked ? 'enable' : 'disable');
        const layer = this.getLayer();

        if (e.checked) {
            try {
                layer.options.scaleRange.setOptions({ id: this.getTypeValue(true) });
            } catch (err) {
                console.log('WARNING: no autozoom level');
            }

            layer.redraw();
        } else {
            layer.hide();
            infoBoard.toggleDashboard(false);
            this._view('reset');
            this.show({ empty: true });
            this._setFilterButtonVisibility(false);
            this._uncheckAll();
            this._manualPaints.clear();
            this._manualFeatures.clear();
            this._removeDeliveredAreasLegend(layer.getMap()._id);
        }

        Workspace.addAction(new Item(`#${$(e).prop('id')}`, e.checked, ControlType.ICheckBox));
    }

    _view(method: string, feature: any) {
        try {
            const instance: any = LayerInfo(this.options.name);
            if (method === 'reset') {
                return instance[method]();
            } else {
                return instance[method](feature);
            }
        } catch (err) {
            console.log(err);
        }
    }

    _getLayerOptions(
        featureCollection: any = this.getLayer().toGeoJSON(),
        themeId: number = (this: any).getThemeValue(),
        id: number
    ) {
        const filterData = filters.get(`filter-${_.kebabCase(this.options.name)}-${this.getThemeValue()}`, id) || {};
        const idPibTheme = 27;
        let defaultSaleType = isAuthorized(FeaturePermission.SYSTEM.showDataRetail) ? 1 : 2;
        defaultSaleType = this._checkIfHasTotalSales() ? 3 : defaultSaleType;

        let typeId;
        try {
            typeId = Number(this.getControls(id).dropdown.list.value);
            if (typeId === environment.get('TYPE.AUTO')) {
                typeId = (this.getZoomLocation('ibge') || {}).id;
            }
        } catch (err) {
            console.log('ERROR: No location type specified');
        }

        if (!filterData.pibYear && themeId === idPibTheme) {
            filterData.pibYear = { text: '2019', value: '2019' };
        }

        const options = {
            idTema: themeId,
            ids: featureCollection.features.map(f => f.properties.Id),
            idTipo: typeId,
            anoMesInicio: this.getDate(id).start.format('YYYYMM'),
            anoMesFim: this.getDate(id).end.format('YYYYMM'),
            idsBandeiras: undefined,
            idsCategorias: undefined,
            defaultSaleType: defaultSaleType,
            saleType: defaultSaleType,
            idsCompanySize: undefined,
            idsFuelType: undefined,
            periodo: themeId === 27 ? 2019 : undefined,
            groupsIds: undefined,
            typeSaleClassificationIds: undefined,
        };

        if (this.options.period.initial && this.options.period.final) {
            options.yearInitial = this.options.period.initial;
            options.yearEnd = this.options.period.final;
        }

        if (filterData && filterData.segmentsVehicleFleet && filterData.segmentsVehicleFleet.value) {
            options.idsCategorias = filterData.segmentsVehicleFleet.value;
        }

        if (filterData && filterData.brandsVehicleFleet && filterData.brandsVehicleFleet.value) {
            options.vehicleBrandIds = _.isArray(filterData.brandsVehicleFleet.value)
                ? filterData.brandsVehicleFleet.value
                : [filterData.brandsVehicleFleet.value];
        }

        if (filterData && filterData.vehicleTypesVehicleFleet && filterData.vehicleTypesVehicleFleet.value) {
            options.vehicleTypeIds = _.isArray(filterData.vehicleTypesVehicleFleet.value)
                ? filterData.vehicleTypesVehicleFleet.value
                : [filterData.vehicleTypesVehicleFleet.value];
        }

        if (filterData && filterData.familiesVehicleFleet && filterData.familiesVehicleFleet.value) {
            options.familyIds = _.isArray(filterData.familiesVehicleFleet.value)
                ? filterData.familiesVehicleFleet.value
                : [filterData.familiesVehicleFleet.value];
        }

        if (filterData && filterData.fuelTypesVehicleFleet && filterData.fuelTypesVehicleFleet.value) {
            options.idsFuelType = _.isArray(filterData.fuelTypesVehicleFleet.value)
                ? filterData.fuelTypesVehicleFleet.value
                : [filterData.fuelTypesVehicleFleet.value];
        }

        if (filterData && filterData.modelsVehicleFleet && filterData.modelsVehicleFleet.value) {
            options.modelIds = _.isArray(filterData.modelsVehicleFleet.value)
                ? filterData.modelsVehicleFleet.value
                : [filterData.modelsVehicleFleet.value];
        }

        if (filterData.brands && filterData.brands.value) {
            options.idsBandeiras = _.isArray(filterData.brands.value)
                ? filterData.brands.value
                : [filterData.brands.value];
        }

        if (!options.idsBandeiras && filterData.classification && _.size(filterData.classification.value)) {
            const classificationIds = _.map(filterData.classification.value, String);

            options.idsBandeiras = _(environment.get('CONFIG.FILTERS.BRANDS'))
                .filter(b => _.includes(classificationIds, String(b.classificationId)))
                .map(b => b.brandId)
                .uniq()
                .value();
        }

        if (filterData.group && filterData.group.value) {
            options.groupsIds = _.isArray(filterData.group.value) ? filterData.group.value : [filterData.group.value];
        }

        if (this._checkThemes(['marketShare', 'openPoint', 'locationName']) && this.brandId) {
            options.idsBandeiras = [this.brandId];
        }

        if (filterData.models && _.size(filterData.models.value)) {
            options.idsCategorias = filterData.models.value;
        } else if (filterData.competingClassification && _.size(filterData.competingClassification.value)) {
            options.idsCategorias = _.flatMap(filterData.competingClassification.value, key => _.map(environment.get('CONFIG.FILTERS.COMPETING_SEGMENTS')[key], 'id'));
        }

        if (this.saleType) {
            options.saleType = this.saleType;
        }

        if (filterData.channels && filterData.channels.value) {
            options.salesChannel = _.isArray(filterData.channels.value)
                ? filterData.channels.value.map(x => x.toString())
                : [filterData.channels.value.toString()];
        }

        if (options.saleType === SaleTypeEnum.Retail) {
            options.salesChannel = [];
        }

        if (filterData.companySizes && _.size(filterData.companySizes.value)) {
            options.idsCompanySize = filterData.companySizes.value;
        }

        if (filterData.fuelTypes && _.size(filterData.fuelTypes.value)) {
            options.idsFuelType = filterData.fuelTypes.value;
        }

        if (filterData.pibTypes && _.size(filterData.pibTypes.value)) {
            options.pibTypes = filterData.pibTypes.value;
        }

        if (filterData.pibYear && filterData.pibYear.value) {
            options.pibYear = filterData.pibYear.value;
            this._date.set(id, { start: moment(`${options.pibYear}-01-01`), end: moment(`${options.pibYear}-12-31`) });
        }

        if (filterData.industry && filterData.industry.value) {
            options.idIndustry = _.isArray(filterData.industry.value)
                ? filterData.industry.value.map(x => x.toString())
                : [filterData.industry.value.toString()];
        }

        if (filterData.typeSocialClass && _.size(filterData.typeSocialClass.value)) {
            options.typeSocialClass = filterData.typeSocialClass.value;
        }

        if (filterData.typeSaleClassification && _.size(filterData.typeSaleClassification.value)) {
            options.typeSaleClassificationIds = filterData.typeSaleClassification.value;
        }

        api.general.log.action(environment._getContextEnum(this.options.name), 'Params', JSON.stringify(options));

        return options;
    }

    _disable() {
        this.getControls().colorpicker.fillColor.input.enable(false);
        this.getControls().colorpicker.fillColor.enable = false;
        try {
            this.getControls().button.filter.input.enable(false);
            this.getControls().button.filter.enable = false;
        } catch (err) {
            console.log('No Filter Button');
        }
        this._updatePopover();
        this._unhighlightArea();
    }

    _changePeriod(date: any, id: number = this.getLayer().getMap()._id) {
        this._date.set(id, {
            start: moment(date.start),
            end: moment(date.end)
        });

        api.general.log.action(environment._getContextEnum(this.options.name), 'Mudou Período', JSON.stringify(date));
        this.reload();
    }

    // trigger event when filtered data
    _filterCallback() {
        if (this.check() && this.getLayer().visible()) {
            api.general.log.action(environment._getContextEnum(this.options.name), 'Mudança Filtro');
            this.reload();
        }
    }

    _toggleFilterByPoints(val: any) {
        api.general.log.action(environment._getContextEnum(this.options.name), 'Pontos nas áreas selecionadas');
        return LayerInfo(this.options.name).setFilterByPoints(Boolean(val));
    }

    _toggleFilterByLocation(val: any) {
        api.general.log.action(environment._getContextEnum(this.options.name), 'Filtrar Área(s) selecionada(s)');
        return LayerInfo(this.options.name).setFilterByLocation(Boolean(val));
    }

    _toggleFilterByAreas(val: any) {
        api.general.log.action(environment._getContextEnum(this.options.name), 'Interseção de áreas selecionadas');
        return LayerInfo(this.options.name).setFilterByAreas(Boolean(val));
    }

    _setFilterButtonVisibility(state: boolean) {
        if (this.options.filter) {
            try {
                this.getControls().button.filter.input.enable(state);
                this.getControls().button.filter.enable = state;

                if (state) {
                    this.getControls().button.filter.type = this.getThemeValue();
                    this.createFilter();
                }

                if (!state) {
                    filters.close();
                }
            } catch (err) {
                console.log('ERROR: ', err);
            }
        }

        if (this._checkThemes(['openPoint', 'locationName', 'invasionIndicator', 'groupsArea'])) {
            state = false;
        }

        try {
            this.getControls().button.overview.input.enable(state);
            this.getControls().button.overview.enable = state;
            if (store.getters[OVERVIEW_PANEL_GET_CURRENT] === this.options.name && !state) {
                dataBoard.hide();
                delete this._overviewFeatures;
            }
        } catch (err) {
            console.log(err);
        }
    }

    _getTagPropertyValue(property: string) {
        switch (property) {
            case 'value':
                const mask = {
                    mask: (_.find(this.options.themes, t => t.id === this.getThemeValue()) || {}).mask
                };
                return _.assign({}, this._tags.tagValue, mask);
            case 'isDrugstore':
                return _.assign({}, this._tags.tagDrugstore);
            default:
                return _.assign({}, this._tags.tagName);
        }
    }

    _getTagStyleScope() {
        return {
            client: environment.get('CONFIG.CLIENT'),
            layerName: this.options.name,
            areaType: this.getTypeValue(true),
            username: sessionStorage.username
        };
    }

    // Options that enable clicking the name tag of a single feature to edit
    // (and persist locally) only that feature's style. Consumed by TagsLayer.
    _tagStyleLayerOptions() {
        return {
            tagEditable: true,
            tagStyleProperty: 'Nome',
            tagStyleScope: this._getTagStyleScope(),
            onTagEdit: (feature: any, baseOptions: any) => openTagStyleEditor({
                title: translate('Common.LocationName'),
                baseOptions,
                feature,
                property: baseOptions.property,
                scope: this._getTagStyleScope(),
                onSaved: () => this._setLayerLabelOptions()
            })
        };
    }

    _tagPositionLayerOptions() {
        return {
            tagLargestPart: true
        };
    }

    _getLayerTag() {
        if (!this.getControls().checkbox) {
            return undefined;
        }

        const tags = [];

        if ((this.getControls().checkbox.tagName || {}).status) {
            tags.push(this._getTagPropertyValue('name'));
        }

        if ((this.getControls().checkbox.tagValue || {}).status) {
            tags.push(this._getTagPropertyValue('value'));
        }

        if ((this.getControls().checkbox.tagDrugstore || {}).status) {
            tags.push(this._getTagPropertyValue('isDrugstore'));
        }

        return tags.length === 1 ? tags[0] : tags;
    }

    _setLayerLabelOptions() {
        this.getLayer().setOptions(_.assign(
            { tag: this._getLayerTag() },
            this._tagStyleLayerOptions(),
            this._tagPositionLayerOptions()
        ));
        if (this.getLayer().visible()) {
            this.getLayer().reload();
        }
    }

    _toggleLabel(isChecked, name) {
        api.general.log.action(
            environment._getContextEnum(this.options.name),
            `${isChecked ? 'Show' : 'Hide'} ${name}`
        );
        this._setLayerLabelOptions();
    }

    _calculateScale(data: any) {
        let source = data.consolidate || data;
        const values = _.map(source, (v: number, k: string) => v);
        const scale = this.getControls().slider.range.value;
        const max = _.max(values);
        const min = _.min(values);
        return {
            min: (Math.abs(min) / 10) * 0.25 * Math.pow(Math.E, 0.3689 * scale) * -1,
            max: (max / 10) * 0.35 * Math.pow(Math.E, 0.3689 * scale)
        };
    }

    _setFeaturesValue(featureCollection: any, values: any) {
        const source = values.consolidate || values;
        const result = {
            type: 'FeatureCollection',
            features: _.map(featureCollection.features, f => {
                f.properties.Total = source[f.properties.Id] ? source[f.properties.Id] : 0;
                f.properties.RegistryDetail = values.detailed ? values.detailed[f.properties.Id] : {};
                return f;
            })
        };

        return result;
    }

    _unhighlightArea(reload: boolean = true) {
        const layer = this.getLayer();

        this._cachePages = new Map();

        layer.setOptions({
            legend: false
        });

        layer.setFeatures(
            this._paintDeliveredAreas(this._setFeaturesValue(layer.toGeoJSON(), {})),
            this.check() && this.getLayer().visible() && reload
        );
    }

    _changeTheme() {
        if (this.getLayer().visible()) {
            this._logAction('Update Tema');
        }

        this._visibleDropdownPeriod();

        if (this.getThemeValue()) {
            this._uncheckAreaPalette();
        }

        this.getControls().colorpicker.fillColor.input.enable(true);
        this.getControls().colorpicker.fillColor.enable = true;
        this.getControls().slider.range.input.enable(true);
        this.getControls().slider.range.enable = true;
        this.getControls().slider.range.input.value(10);
        this.getControls().slider.range.value = 10;

        this.getControls().slider.opacity.input.value(5);
        this.getControls().slider.opacity.value = 5;

        if (this.options.name !== 'Voronoi') {
            this._loadPeriodByTheme();
            this._updatePopover();
            this.reload();
        }
    }

    _visibleDropdownPeriod() {
        const isVehicleFleet = this._checkThemes(['vehicleFleetArea', 'repairableFleetArea']);
        const hasFilterConfig = environment.get('CONFIG.FILTERS.FILTERBY');

        if (isVehicleFleet && hasFilterConfig) {
            this.getControls().dropdown.periods.input.enable(true);
            this.getControls().dropdown.periods.enable = true;
            this.getControls().dropdown.periods.input.wrapper.show();
        } else {
            this.getControls().dropdown.periods.input.enable(false);
            this.getControls().dropdown.periods.enable = false;
            this.getControls().dropdown.periods.input.wrapper.hide();
        }

        const layerPrefix = this.options.name.toLowerCase();
        const $dateLink = $(`#${layerPrefix}-links #layer-customers-date`).closest('.actions-checkbox');

        if ($dateLink.length) {
            if (isVehicleFleet) {
                $dateLink.hide();
            } else {
                $dateLink.show();
            }
        }
    }

    _colorizeStaticShape(id: number, featureCollection: any = this.getLayer().toGeoJSON(), values: any) {
        // console.time('NeoGIG.Feature.Shapes -> _colorizeStaticShape');
        const filterData = filters.get(`filter-${_.kebabCase(this.options.name)}-${this.getThemeValue()}`, id);
        if (!_.size(values)) {
            this._cachePages.set(id, undefined);
            return featureCollection;
        }

        this._cachePages.set(id, values);

        const { symbology } = this.getLayer().options;
        const scale = this._calculateScale(this._cachePages.get(id));

        const legend = {};

        // is type of Market Share
        if (this._checkThemes(['marketShare'], id)) {
            legend.title = `${this.getThemeText(id).replace(' (%)', '')} ${
                filterData.brands && filterData.brands.text
                    ? _.capitalize(filterData.brands.text)
                    : environment.get('CONFIG.CLIENT')
            } (%)`;
        } else {
            legend.title = this.getThemeText(id);
        }

        const date = this.getDate(id);
        if (
            this._checkThemes(
                [
                    'industryRegistration',
                    'brandsRegistration',
                    'marketShare',
                    'invasionIndicator',
                    'unmetDemandForSales',
                    'unmetDemandForExpansion'
                ],
                id
            )
        ) {
            let saleTypeName = '';
            let defaultSaleType =
                (isAuthorized('Direct-sales-filters-sale-type') && isAuthorized('Direct-sales-filters-channels')) ||
                isAuthorized('Direct-sales-filters-channels')
                    ? 2
                    : 1;
            if (isAuthorized('Direct-sales-filters-sale-type') && isAuthorized('Direct-sales-filters-channels')) {
                defaultSaleType = 1;
            }

            if (this._checkIfHasTotalSales()) {
                defaultSaleType = 3;
                saleTypeName = translate('Common.Total');
            }

            if (defaultSaleType === 1) {
                saleTypeName = translate('Common.Retail');
            }

            if (defaultSaleType === 2) {
                saleTypeName = translate('Common.DirectSales');
            }

            if (filterData.saleType && filterData.saleType.text) saleTypeName = filterData.saleType.text;
            if (this.saleType) {
                switch (this.saleType) {
                    case SaleTypeEnum.Retail:
                        saleTypeName = translate('Common.Retail');
                        break;
                    case SaleTypeEnum.DirectSales:
                        saleTypeName = translate('Common.Direct');
                        break;
                    case SaleTypeEnum.Total:
                        saleTypeName = translate('Common.Total');
                        break;
                    default:
                        break;
                }
            }

            legend.subtitle = `${saleTypeName} | ${date.start.format('MM/YYYY')} - ${date.end.format('MM/YYYY')}`;
        } else {
            legend.subtitle = `${date.start.format('MM/YYYY')} - ${date.end.format('MM/YYYY')}`;
        }

        legend.mask = (_.find(this.options.themes, t => t.id === +this.getThemeValue(id)) || {}).mask;

        if (this._checkThemes(['openPoint', 'locationName', 'socialClass', 'groupsArea', 'brandArea', 'segmentLeader'], id)) {
            legend.type = 'uniq';
        }

        if (this._checkThemes(['ipcAuto'], id)) {
            symbology.pushValueToRight = [0];
        } else {
            delete symbology.pushValueToRight;
        }

        symbology.max = scale.max;
        symbology.min = scale.min;
        if (this._checkThemes(['groupsArea', 'brandArea', 'socialClass', 'segmentLeader'])) {
            legend.subtitle = '';
            legend.mask = "";
        }

        symbology.style.fillOpacity = 0.5;

        if (this._checkThemes(['socialClass'])) {
            this._configureSocialClassSymbology(symbology);
        }

        if (this._checkThemes(['vehicleFleetArea', 'repairableFleetArea'])) {
            legend.subtitle = `${this.options.period.initial} até ${this.options.period.final}`;
        }

        this.getLayer(id).setOptions({ legend, symbology });
        this._setLayerLabelOptions();
        return this._setFeaturesValue(featureCollection, this._cachePages.get(id));
    }

    _configureSocialClassSymbology(symbology) {
        const socialClassThemeColors = [
            'rgb(217,95,95)',
            'rgb(224,135,123)',
            'rgb(235,175,155)',
            'rgb(245,211,191)',
            'rgb(255,211,229)',
            'rgb(255,241,227)'
        ];
        symbology.theme = socialClassThemeColors;
        symbology.style.fillOpacity = 0.8;
        symbology.style.weight = 0.5;

        return symbology;
    }

    _checkThemes(themes, id, value) {
        const validThemes = themes.filter(p => this.options.themes[p]).map(p => this.options.themes[p].id);
        return _.some(validThemes, v => v === (value || this.getThemeValue(id)));
    }

    _getCheckboxOptions() {
        const res = {};
        if (
            _(this.options.types)
                .filter((val, key) => key !== 'DINAMICO')
                .size()
        ) {
            res[translate('Common.LocationName')] = {
                name: 'tagName',
                displaySection: true,
                firstOfSection: true,
                change: isChecked => this._toggleLabel(isChecked, 'tagName'),
                style: {
                    change: (tag: any) => {
                        this._tags.tagName = tag;
                        this._toggleLabel();
                    },
                    clearTagStyles: () => {
                        clearTagStyles(this._getTagStyleScope(), 'Nome');
                        this._setLayerLabelOptions();
                    },
                    options: this._tags.tagName
                }
            };
        }

        if (this.hasDynamicThemes()) {
            res[translate('Common.ThemeValue')] = {
                name: 'tagValue',
                displaySection: true,
                change: isChecked => this._toggleLabel(isChecked, 'tagValue'),
                style: {
                    change: (tag: any) => {
                        this._tags.tagValue = tag;
                        this._toggleLabel();
                    },
                    options: this._tags.tagValue
                }
            };

            if (isAuthorized(FeaturePermission.MAP_THEME.drugStore) && this.options.name === 'MarkedArea') {
                res['Exibir Drugstore'] = {
                    name: 'tagDrugstore',
                    displaySection: false,
                    change: isChecked => this._toggleLabel(isChecked, 'tagDrugstore'),
                    style: {
                        change: (tag: any) => {
                            this._tags.tagValue = tag;
                            this._toggleLabel();
                        },
                        options: this._tags.tagDrugstore
                    }
                };
            }

            if (this.options.name === 'MarkedArea' && isAuthorized(FeaturePermission.LAYER_AREA_CLIENT.showAdditionalArea)) {
                res[translate('Action.ShowAdditionalArea')] = {
                    name: 'additionalArea',
                    displaySection: true,
                    change: isChecked => this._toggleAdditionalArea(isChecked),
                    style: {
                        click: () => this._changeAdditionalArea()
                    }
                };
            }

            if (isAuthorized(FeaturePermission.LAYER_SELECTED_AREA.manualColorful)) {
                res[translate('Common.AreasSelected')] = {
                    name: 'tagChangeColor',
                    displaySection: true,
                    change: {},
                    style: {
                        change: ({ color }) => {
                            this._paintSelection(color);
                        },
                        isColor: true
                    }
                };
            }

            if (isAuthorized(FeaturePermission.LAYER_SELECTED_AREA.manualColorful)) {
                res[translate('Common.ManualAreasColor')] = {
                    name: 'tagAreaPalette',
                    displaySection: true,
                    change: isChecked => this.togglePaintDeliveredAreas(isChecked)
                };
            }
        }

        if (
            _(this.options.types)
                .filter((val, key) => key !== 'DINAMICO')
                .size()
        ) {
            res[translate('Common.SelectedLocation')] = {
                name: 'filterLocation',
                filterSection: true,
                firstOfSection: true,
                change: this._toggleFilterByLocation
            };
            res[translate('Common.PointsInsideLocations')] = {
                name: 'filterPoints',
                filterSection: true,
                change: this._toggleFilterByPoints
            };
            res[translate('Common.AreasInsideLocations')] = {
                name: 'filterAreas',
                filterSection: true,
                change: this._toggleFilterByAreas
            };
        }

        return res;
    }

    _paintSelection(color) {
        const selectedLayer = this.getLayer();

        const selectedAreaLayer = selectedLayer
            .toGeoJSON()
            .features.filter(f => require('../info').get('SelectedAreas').check(f.properties.Id));

        const markedAreasLayer = selectedLayer
            .toGeoJSON()
            .features.filter(f => require('../info').get('MarkedArea').check(f.properties.Id));

        let selectedFeatures = selectedAreaLayer;

        if (!selectedFeatures.length) {
            selectedFeatures = markedAreasLayer;
        }

        if (!selectedFeatures.length) {
            notify(NOTIFY_TYPE.ERROR, translate('Common.AnyAreasSelected'));
            return;
        }

        const group = d3.select(`#${selectedLayer.id}`);

        selectedFeatures.forEach(f => {
            const id = f.properties.Id;
            if (!id) return;

            this._manualPaints.set(id, color);
            this._manualFeatures.set(id, _.cloneDeep(f));
            f.properties._fillColor = color;

            if (!group.empty()) {
                group.selectAll('path').each(function () {
                    const data = this.__data__;
                    if (!data || !data.properties) return;

                    const props = data.properties;
                    if (props.Id === id) {
                        d3.select(this).transition().duration(300).attr('fill', color);
                    }
                });
            }
        });

        selectedFeatures.forEach(f => {
            const id = f.properties.Id;
            if (!id) return;

            $(`#SelectedAreas-selection-${id}`).iCheck('uncheck');
            $(`#MarkedArea-selection-${id}`).iCheck('uncheck');
        });

        this._refreshDeliveredAreasLegend(selectedLayer.getMap()._id);
    }

    getAreaPalette(id: number = this.getLayer().getMap()._id) {
        return this._areaPalettes.get(id) || ColorBrewer.segments.extra;
    }

    isPaintingDeliveredAreas(id: number = this.getLayer().getMap()._id) {
        try {
            return !!this.getControls(id).checkbox.tagAreaPalette.status;
        } catch (err) {
            return false;
        }
    }

    togglePaintDeliveredAreas(isChecked: boolean) {
        if (this._revertingAreaPalette) {
            this._revertingAreaPalette = false;
            return;
        }

        if (isChecked && this.getThemeValue()) {
            notify(NOTIFY_TYPE.ERROR, translate('Validation.ManualColorWithoutTheme'));
            this._uncheckAreaPalette();
            return;
        }

        this.reload();
    }

    _uncheckAreaPalette() {
        if (!this.isPaintingDeliveredAreas()) {
            return;
        }

        _.defer(() => {
            try {
                this._revertingAreaPalette = true;
                this.getControls().checkbox.tagAreaPalette.input.iCheck('uncheck');
            } catch (err) {
                this._revertingAreaPalette = false;
            }
        });
    }

    _getAreaPaletteColor(featureId: any, palette: any) {
        const size = _.size(palette);

        if (!size) {
            return undefined;
        }

        const numeric = Number(featureId);
        const index = _.isFinite(numeric)
            ? Math.abs(_.toInteger(numeric))
            : _.reduce(String(featureId), (acc, char) => acc + char.charCodeAt(0), 0);

        return palette[index % size];
    }

    _paintDeliveredAreas(
        featureCollection: any,
        id: number = this.getLayer().getMap()._id,
        deliveredIds: any = null
    ) {
        const palette = this.getAreaPalette(id);
        const isPainting = this.isPaintingDeliveredAreas(id) && !this.getThemeValue(id);
        const legendItems = [];

        _.forEach(_.get(featureCollection, 'features', []), f => {
            const featureId = (f.properties || {}).Id;
            const isDelivered = !deliveredIds || deliveredIds.has(String(featureId));

            if (isPainting && isDelivered) {
                f.properties._fillColor = this._getAreaPaletteColor(featureId, palette);
                legendItems.push({ id: featureId, text: f.properties.Nome, color: f.properties._fillColor });
            } else {
                delete f.properties._fillColor;
            }
        });

        this._renderDeliveredAreasLegend(id, legendItems);

        return featureCollection;
    }

    _renderDeliveredAreasLegend(id: number, items: any) {
        this._removeDeliveredAreasLegend(id);

        if (!_.size(items)) {
            return;
        }

        const legend = new CustomLegendLayer({
            map: worldMap.get(id),
            title: _.get(this.getControls(id), 'dropdown.list.text', ''),
            type: 'polygon',
            items: _.sortBy(
                _.map(items, item => ({ text: item.text, color: this._manualPaints.get(item.id) || item.color })),
                'text'
            )
        });

        this._deliveredAreasLegends.set(id, { legend, items });
    }

    _refreshDeliveredAreasLegend(id: number) {
        const entry = this._deliveredAreasLegends.get(id);

        if (entry) {
            this._renderDeliveredAreasLegend(id, entry.items);
        }
    }

    _removeDeliveredAreasLegend(id: number) {
        const entry = this._deliveredAreasLegends.get(id);

        if (entry) {
            entry.legend.destroyed = true;
            entry.legend.remove();
            this._deliveredAreasLegends.delete(id);
        }
    }

    _applyManualColors(layer = this.getLayer()) {
        const group = d3.select(`#${layer.id}`);
        if (group.empty()) return;

        group.selectAll('path').each(
            function () {
                const data = this.__data__;
                if (!data || !data.properties) return;

                const id = data.properties.Id;
                if (!id) return;

                const fill = this._manualPaints.get(id);
                if (fill) {
                    d3.select(this).transition().duration(300).attr('fill', fill);
                }
            }.bind(this)
        );
    }

    _checkIfHasTotalSales() {
        return environment.get("CONFIG.CLIENT_TOTAL_SALES");
    }

    _mountNameChartPie(isBrandAreaOrSegmentLeader) {
        if (this._checkThemes(['pib'])) {
            return `RANKING ${translate('Common.PIB')} (%)`;
        }

        if (!isBrandAreaOrSegmentLeader) {
            return `RANKING MARKET SHARE (%)`;
        }

        const entity = this._checkThemes(['brandArea']) ? 'Common.Brands' : 'Common.Segments';

        return `RANKING ${translate(entity)} (% ${translate('Common.Areas')})`;
    }

    _calculatePibPercentage(features, total) {
        return _(features)
            .filter(f => f.properties.Total)
            .orderBy(f => f.properties.Total)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.properties.Nome,
                [translate('Common.Total')]: f.properties.Total / total
            }))
            .value();
    }

    _mountTotalColumnTitle(isBrandAreaOrSegmentLeader) {
        if (!isBrandAreaOrSegmentLeader) {
            return translate('Common.Total');
        }

        return this._checkThemes(['brandArea'])
            ? translate('Common.MarketShare')
            : `${translate('Common.Total')} (%)`;
    }

    _getAreaThemePalette() {
        return this._checkThemes(['brandArea']) ? ColorBrewer.brands : getSegmentTheme();
    }
}

export default ShapesControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/ShapesControl.js